import React from 'react';

import {configureMapColumnNamesModalPane} from 'bulk-import/panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from 'bulk-import/panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import {getColumnNamesForImport} from 'bulk-import/results/GetColumnNamesForImport';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from 'bulk-import/SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {DataTable, ImportedRowUpdate} from 'notebook/data-tables/DataTableConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

interface BulkUpdateDataTableFlowProps {
  dataTableKeyFields: Array<COLUMN_TYPE>;
  onRequestClose: () => void;
  dataTable: DataTable;
  onImportRows: (rows: Array<ImportedRowUpdate>) => void;
}

/**
 * Provides the UX and hooks for allowing users to update rows in a data table via spreadsheet import.
 *
 * This component does not itself update the data table directly. Instead, it imports rows which can
 * then be used to update the table by calling `onImportRows`.
 */
export default class BulkUpdateDataTableFlow extends React.Component<BulkUpdateDataTableFlowProps> {
  static open(props: Omit<BulkUpdateDataTableFlowProps, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkUpdateDataTableFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  private getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({
        getExampleImageURL() {
          return SpreadsheetBulkImportUtil.getExampleImageURLForTagSchema(null);
        },
      }),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes() {
          return [COLUMN_TYPE.DATA_TABLE_MATCHER, COLUMN_TYPE.DATA_TABLE_VALUE, COLUMN_TYPE.IGNORE];
        },
        getRequiredColumnsError(
          columns: Array<SpreadsheetColumn>,
          {selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}
        ) {
          const numMatchColumns = columns.filter(({type}) => type === COLUMN_TYPE.DATA_TABLE_MATCHER).length;
          if (numMatchColumns === 0) {
            return 'At least one Match field is required.';
          }
          const numSetColumns = columns.filter(({type}) => type === COLUMN_TYPE.DATA_TABLE_VALUE).length;
          if (numSetColumns === 0) {
            return 'At least one Set field is required.';
          }
          const tableColumnNames = columns.map(({tableColumnName}) => tableColumnName).filter((name) => name);
          if (new Set(tableColumnNames).size < tableColumnNames.length) {
            return 'Each column must be selected at most once.';
          }
          return null;
        },
        onValidateSpreadsheet: () => {
          SpreadsheetBulkImportActions.validateUpdatedRowsForDataTableImport(this.props.dataTable);
        },
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: false,
        enableSkipErrors: false,
      }),
    },
  ];

  private handleImportFromSpreadsheet = (): void => {
    SpreadsheetBulkImportActions.getUpdatedRowsForDataTableImport(
      this.props.dataTable,
      this.props.onImportRows
    );
  };

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importType={IMPORT_TYPE.DATA_TABLE}
        initialSettings={{
          initialTableColumnNames: getColumnNamesForImport(this.props.dataTable.config),
          initialDataTableKeyFields: this.props.dataTableKeyFields,
        }}
        onImportFromSpreadsheet={this.handleImportFromSpreadsheet}
        onRequestClose={this.props.onRequestClose}
        panes={this.getPanes()}
        title={`Bulk update ${MODEL_NAMES.result_table}`}
      />
    );
  }
}
